/**
 * ExpandableEditorDialog
 *
 * Full-height Fluent Dialog that wraps a single `TokenizedInput` for
 * viewing/editing long-form text that has been clamped in the drawer.
 * The panel and dialog editors share state via `value`/`onChange`, so
 * edits sync live in both directions and there is no "commit on close"
 * step — closing the dialog just dismisses the overlay.
 *
 * Used by `TextAreaField` and `AgentInstructionField` for the System
 * message and any other long-form multiline parameter that would
 * otherwise push the rest of the drawer off-screen.
 */

import { useId } from 'react';
import {
  makeStyles,
  tokens,
  Dialog,
  DialogSurface,
  DialogBody,
  Button,
  Text,
  Tooltip,
} from '@coreai/fluentui-react';
import { DismissRegular } from '@fluentui/react-icons';
import { TokenizedInput } from './TokenizedInput';
import type { AgentParameterSchema } from './AgentParameterMenu';
import type { ValueSegment } from '@/types/valueSegment';

const useStyles = makeStyles({
  surface: {
    // Give the dialog plenty of room. Fluent's default surface is fixed
    // width; we override so the expanded editor breathes.
    width: 'min(880px, 92vw)',
    maxWidth: '92vw',
    height: 'min(720px, 88vh)',
    maxHeight: '88vh',
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  body: {
    // Fluent's DialogBody defaults to a CSS grid with row-gap. We need a
    // vertical flex column with zero gap so the header sits flush against
    // the editor area and the editor grows to fill the surface.
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    padding: 0,
    gap: 0,
    rowGap: 0,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    flexShrink: 0,
    gap: tokens.spacingHorizontalM,
  },
  headerLabel: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  editorArea: {
    // Fills the remaining dialog height. The nested TokenizedInput inherits
    // 100% height via display:flex; its editor grows to fill this box.
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    paddingTop: tokens.spacingVerticalM,
    paddingRight: tokens.spacingHorizontalL,
    paddingBottom: tokens.spacingVerticalL,
    paddingLeft: tokens.spacingHorizontalL,
    minHeight: 0,
  },
  editorHost: {
    // Fills the remaining dialog height. The nested TokenizedInput opts into
    // `fillParent` (a stable prop) to stretch + scroll internally — no DOM
    // coupling to TokenizedInput's internal structure.
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    width: '100%',
  },
});

export interface ExpandableEditorDialogProps {
  /** Whether the dialog is open. */
  open: boolean;
  /** Called when the dialog requests to close (X, ESC, backdrop). */
  onClose: () => void;
  /** Field label shown in the dialog header. */
  label: string;
  /** Current segments value (shared with the panel editor). */
  value: ValueSegment[];
  /** Called when the user edits the value. */
  onChange: (segments: ValueSegment[]) => void;
  /** Placeholder passed through to the inner TokenizedInput. */
  placeholder?: string | undefined;
  /** Disabled state passed through. */
  disabled?: boolean | undefined;
  /** Whether to show the agent parameter button. */
  showAgentParameterButton?: boolean | undefined;
  /** Agent parameter schema for tool inputs. */
  agentParameterSchema?: AgentParameterSchema | undefined;
  /** Action outputs from the last run, for autocomplete type inference. */
  actionOutputs?: Record<string, unknown> | undefined;
}

/**
 * ExpandableEditorDialog - Full-height overlay hosting a TokenizedInput.
 *
 * The dialog does not own the value; edits stream through `onChange` to the
 * caller (typically field state). Callers should *unmount* the panel editor
 * while the dialog is open — two live-synced TokenizedInputs on the same
 * `value` corrupt each other's undo stack on every keystroke.
 *
 * Focus restoration on close is the *caller's* responsibility (via
 * `useRestoreFocusOnDialogClose`): the trigger button unmounts with the panel
 * editor, so it can't be captured/restored from here — see that hook for why.
 */
export const ExpandableEditorDialog = ({
  open,
  onClose,
  label,
  value,
  onChange,
  placeholder,
  disabled,
  showAgentParameterButton,
  agentParameterSchema,
  actionOutputs,
}: ExpandableEditorDialogProps) => {
  const styles = useStyles();
  const generatedId = useId();
  const titleId = `expandable-editor-title-${generatedId}`;

  return (
    <Dialog
      open={open}
      onOpenChange={(_, data) => {
        if (!data.open) onClose();
      }}
      modalType="modal"
    >
      <DialogSurface
        className={styles.surface}
        aria-labelledby={titleId}
        // Fluent's Escape handler calls preventDefault() but not
        // stopPropagation(), so an Escape keydown bubbles up the React tree to
        // the Node Details drawer's own Escape handler and closes the whole
        // drawer along with this dialog. Stop propagation here (Fluent invokes
        // this onKeyDown *before* its own close logic, so the dialog still
        // closes) to scope Escape to just the dialog — the same layered
        // dismissal the drawer already does for its connection picker.
        onKeyDown={(e) => {
          if (e.key === 'Escape') e.stopPropagation();
        }}
      >
        <DialogBody className={styles.body}>
          <div className={styles.header}>
            <Text id={titleId} className={styles.headerLabel}>
              {label}
            </Text>
            <Tooltip content="Close" relationship="label">
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                onClick={onClose}
                aria-label="Close"
              />
            </Tooltip>
          </div>
          <div className={styles.editorArea}>
            <div className={styles.editorHost}>
              <TokenizedInput
                name={`${titleId}-editor`}
                ariaLabel={label}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                multiline={true}
                disabled={disabled}
                readonly={false}
                autoFocus={true}
                fillParent={true}
                showAgentParameterButton={showAgentParameterButton}
                agentParameterSchema={agentParameterSchema}
                actionOutputs={actionOutputs}
              />
            </div>
          </div>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

ExpandableEditorDialog.displayName = 'ExpandableEditorDialog';
